/**
 * CMS Utility Functions
 *
 * Utility functions for processing CMS data including template resolution,
 * link parsing, and component transformations.
 */

import { Card, CMSBanner, HomePage, Section, Title } from '@/@cms/types/entities';
import { ValidationResult } from '@/@cms/types/validators';

/**
 * Resolves template placeholders in text using provided values
 *
 * @param text - Text containing placeholders
 * @param values - Dictionary of values to replace placeholders
 * @returns Text with placeholders replaced with actual values
 *
 * @example
 * ```typescript
 * const text = "Hello {{user.name}}!";
 * const resolved = resolveTemplates(text, { 'user.name': 'John' });
 * // Result: "Hello John!"
 * ```
 */
export function resolveTemplates(text: string, values: Record<string, string>): string {
    if (!text) return '';

    let result = text;

    // Replace all placeholders with their values
    Object.entries(values).forEach(([key, value]) => {
        const placeholder = `{{${key}}}`;
        result = result.replace(new RegExp(placeholder, 'g'), value);
    });

    return result;
}

/**
 * Creates a template resolver function with provided values
 *
 * @param values - Dictionary of values to replace placeholders
 * @returns Function that resolves templates
 *
 * @example
 * ```typescript
 * const resolver = createTemplateResolver({
 *   'user.name': 'John',
 *   'studio': 'https://studio.example.com'
 * });
 * const resolved = resolver("Welcome, {{user.name}}!");
 * // Result: "Welcome, John!"
 * ```
 */
export function createTemplateResolver(values: Record<string, string>) {
    return (text: string) => resolveTemplates(text || '', values);
}

/**
 * Gets default template values for the studio application
 *
 * @param userName - User's name to display (defaults to 'User')
 * @param studioUrl - Base URL for the studio application (defaults to empty string)
 * @returns Object containing default template values
 */
export function getDefaultTemplateValues(
    userName: string = 'User',
    studioUrl: string = '',
): Record<string, string> {
    return {
        'user.name': userName.split(' ')[0],
        studio: studioUrl,
        portal: '', // Default for portal links
        currentDate: new Date().toLocaleDateString(),
    };
}

/**
 * Parses navigation links to handle special variables
 *
 * @param link - The original link string
 * @param studioUrl - The studio URL to replace {{studio}} placeholders
 * @returns The processed link with variables replaced
 */
export function parseNavigationLink(link: string, studioUrl: string = ''): string {
    if (!link) return '';

    return resolveTemplates(link, {
        studio: studioUrl,
        portal: '',
    });
}

/**
 * Process card data to replace template variables in specific fields
 *
 * @param card - The card data to process
 * @param templateValues - Values for template variables
 * @returns Processed card with templates resolved
 */
export function processCardTemplates(card: Card, templateValues: Record<string, string>): Card {
    if (!card) return card;

    const resolver = createTemplateResolver(templateValues);

    return {
        ...card,
        TitleText: resolver(card.TitleText),
        BodyText: card.BodyText ? resolver(card.BodyText) : card.BodyText,
        Link: card.Link ? resolver(card.Link) : card.Link,
    };
}

/**
 * Process banner data to replace template variables in specific fields
 *
 * @param banner - The banner data to process
 * @param templateValues - Values for template variables
 * @returns Processed banner with templates resolved
 */
export function processBannerTemplates(
    banner: CMSBanner,
    templateValues: Record<string, string>,
): CMSBanner {
    if (!banner) return banner;

    const resolver = createTemplateResolver(templateValues);

    return {
        ...banner,
        CTA: resolver(banner.CTA),
        Tagline: banner.Tagline ? resolver(banner.Tagline) : banner.Tagline,
        Action: banner.Action
            ? {
                  ...banner.Action,
                  Link: resolver(banner.Action.Link),
                  Text: resolver(banner.Action.Text),
              }
            : banner.Action,
    };
}

/**
 * Process title data to replace template variables in specific fields
 *
 * @param title - The title data to process
 * @param templateValues - Values for template variables
 * @returns Processed title with templates resolved
 */
export function processTitleTemplates(title: Title, templateValues: Record<string, string>): Title {
    if (!title) return title;

    const resolver = createTemplateResolver(templateValues);

    return {
        ...title,
        Title: resolver(title.Title),
        Subtitle: title.Subtitle ? resolver(title.Subtitle) : title.Subtitle,
    };
}

/**
 * Filters cards in sections based on feature flags
 *
 * @param sections - Array of sections containing cards
 * @param isFeatureEnabled - Function to check if a feature is enabled
 * @returns Sections with filtered cards
 */
export function filterSectionCards(sections: Section[]): Section[] {
    if (!sections || !Array.isArray(sections)) {
        return [];
    }

    return sections.map((section) => {
        if (!section.Cards || !Array.isArray(section.Cards)) {
            return section;
        }

        // Return a new section with filtered cards
        return {
            ...section,
            Cards: section.Cards,
        };
    });
}

/**
 * Processes a home page data object, validating and filtering by feature flags
 *
 * @param data - Home page data from CMS
 * @param isFeatureEnabled - Function to check if a feature is enabled
 * @returns Processed home page data
 */
export function processHomePageData(
    data: unknown,
    isFeatureEnabled: (flagName: string) => boolean,
): ValidationResult<HomePage> {
    // Basic structure check
    if (!data || typeof data !== 'object') {
        return {
            valid: false,
            errors: ['Home page data must be an object'],
        };
    }

    // Check for required properties
    const homeData = data as any;

    if (!homeData.data) {
        return {
            valid: false,
            errors: ['Missing data property in home page data'],
        };
    }

    // Extract the actual home page data
    const homePageData = homeData.data;

    // Basic validation for header components
    if (!Array.isArray(homePageData.Header)) {
        return {
            valid: false,
            errors: ['Header must be an array of components'],
        };
    }

    // Basic validation for sections
    if (!Array.isArray(homePageData.Sections)) {
        return {
            valid: false,
            errors: ['Sections must be an array'],
        };
    }

    // Create a processed home page object with filtered components
    const processedHomePage: HomePage = {
        ...homePageData,
        Header: homePageData.Header,
        Sections: homePageData.Sections,
    };

    return {
        valid: true,
        data: processedHomePage,
    };
}

/**
 * Determines if a component should be visible based on visibility rules
 *
 * @param component - Component with potential visibility rules
 * @param currentUser - Current user information including role and client
 * @returns True if component should be visible, false otherwise
 */
export function shouldShowComponent(
    component: any,
    currentUser: { role?: string; client?: string } = {},
): boolean {
    // If component has no visibility rules, show it
    if (
        !component.Visibility ||
        !Array.isArray(component.Visibility) ||
        component.Visibility.length === 0
    ) {
        return true;
    }

    // Check each visibility rule
    return component.Visibility.some((rule: any) => {
        // Public is always visible
        if (rule.Type === 'Public') {
            return true;
        }

        // Internal requires a staff role
        if (rule.Type === 'Internal') {
            return currentUser.role === 'admin' || currentUser.role === 'staff';
        }

        // Client-specific visibility
        if (rule.Type === 'Client') {
            return rule.Client === currentUser.client;
        }

        return false;
    });
}
